/**
 * One set of rules for typing a court date into a form, wherever that form lives.
 *
 * Three screens create court events — the client screen, the case screen, and now the calendar —
 * and each carried its own copy of the same validation and the same date arithmetic. Three copies of
 * a rule is three chances for them to disagree about what is required, which is how one screen ends
 * up accepting a record another would refuse.
 *
 * ## 🔴 The times are COURT wall-clock, not the browser's
 *
 * A notice says "9:00 AM" and means nine in the morning in a Texas courtroom. `new Date("2026-09-25T09:00")`
 * parses in whatever zone the laptop is set to, so the same typed time produced a different instant
 * depending on who typed it. The backend hit this exact bug and fixed it in `utils/courtTimezone.ts`
 * — *"the previous code did `new Date(\`${date}T${time}\`)`, which parses in the SERVER zone"* — and
 * the browser had the same defect for the same reason.
 *
 * The zone mirrors `DEFAULT_COURT_TIMEZONE` on the server. It is stated in both places rather than
 * fetched, because a form cannot wait on a round trip to know what a typed time means, and a court
 * in El Paso is the firm's problem to decide rather than this module's to guess.
 */
import { DateTime } from 'luxon';

/** Must match DEFAULT_COURT_TIMEZONE in backend/src/utils/courtTimezone.ts. */
export const COURT_TIME_ZONE = 'America/Chicago';

export interface CourtEventFormFields {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  title: string;
}

/**
 * A typed date and time as an epoch in MILLISECONDS, read as court wall-clock.
 *
 * Milliseconds is not a detail: the create route refuses anything below a floor because 452 rows
 * were once written in seconds and rendered as January 1970.
 *
 * Returns null when the pair cannot be read at all, so a caller reports that rather than sending
 * `NaN` and getting a validation error it cannot explain.
 */
export function courtWallClockToMillis(date: string, time: string): number | null {
  const parsed = DateTime.fromISO(`${date}T${time}`, { zone: COURT_TIME_ZONE });
  return parsed.isValid ? parsed.toMillis() : null;
}

/**
 * Half an hour after the start, as a date and a time.
 *
 * Court settings rarely state an end, and asking for one on every row of a docket is the friction
 * that sends people back to Clio. It rolls the date over: an evening setting whose end lands after
 * midnight must move to the next day, or the form refuses its own suggestion.
 */
export function defaultEnd(
  startDate: string,
  startTime: string,
): { endDate: string; endTime: string } | null {
  const start = DateTime.fromISO(`${startDate}T${startTime}`, { zone: COURT_TIME_ZONE });
  if (!start.isValid) return null;
  const end = start.plus({ minutes: 30 });
  return { endDate: end.toFormat('yyyy-MM-dd'), endTime: end.toFormat('HH:mm') };
}

/**
 * The first thing wrong with the form, phrased for the person looking at it, or null when it is
 * good to save.
 *
 * A single "check all fields" message is what made the old dialog impossible to clear: it never said
 * which field was the problem. Description is deliberately not required — a notice often has nothing
 * to add beyond the date, the time and what the setting is, and demanding prose invents busywork.
 */
export function courtEventFormProblem(form: CourtEventFormFields): string | null {
  if (!form.startDate.trim()) return 'Enter a start date.';
  if (!form.startTime.trim()) return 'Enter a start time.';
  if (!form.endDate.trim()) return 'Enter an end date.';
  if (!form.endTime.trim()) return 'Enter an end time.';
  if (!form.title.trim()) return 'Enter a title for the event.';

  const start = courtWallClockToMillis(form.startDate, form.startTime);
  const end = courtWallClockToMillis(form.endDate, form.endTime);
  if (start === null || end === null) return 'Check the date and time.';
  if (end <= start) return 'The end time must be after the start time.';

  return null;
}
